/**
 * Oath Forms Service
 * API calls for forms browsing, ordering, and management
 * Routes to /oath/forms/* endpoints in the backend
 */

import { OathStorageKeys } from '../models/userModels';
import { oathRefreshToken } from './oathAuthService';

const BASE_URL = process.env.REACT_APP_BASE_URL;

/**
 * Get auth headers with JWT token
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem(OathStorageKeys.JWT_TOKEN);
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

/**
 * Authenticated fetch wrapper with auto-refresh on 401
 */
const authFetch = async (url, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
  });

  if (response.status === 401) {
    const refreshResult = await oathRefreshToken();
    if (refreshResult.success) {
      return fetch(url, {
        ...options,
        headers: {
          ...getAuthHeaders(),
          ...options.headers,
        },
      });
    }
  }

  return response;
};

/**
 * Parse API response - handles BaseResponse wrapper
 */
const parseResponse = async (response) => {
  const data = await response.json().catch(() => ({}));
  return data.result !== undefined ? data.result : data;
};

// ============================================
// FORM BROWSING
// ============================================

/**
 * Get all forms available to the authenticated agent
 * Returns forms split into state-specific and general (Bankers) lists
 * @param {number} agencyId - Agency ID
 * @returns {Promise<{success: boolean, data?: {stateFormList: Array, bankersFormList: Array, states: string[]}, error?: string}>}
 */
export const getForms = async (agencyId) => {
  try {
    const params = new URLSearchParams({
      agencyId: agencyId.toString(),
    });

    const response = await authFetch(`${BASE_URL}oath/forms?${params}`);

    // Handle 204 No Content — treat as empty but successful response
    if (response.status === 204) {
      return { success: true, data: { stateFormList: [], bankersFormList: [], states: [] } };
    }

    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to load forms' };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('getForms error:', error);
    return { success: false, error: 'Network error loading forms' };
  }
};

/**
 * Get a form's PDF content as base64 for inline viewing
 * @param {number} formId - Form ID
 * @param {number} agencyId - Agency ID (needed for dynamic PDF receipt number)
 * @returns {Promise<{success: boolean, data?: {formBytes: string, fileType: string}, error?: string}>}
 */
export const getFormPdf = async (formId, agencyId) => {
  try {
    const params = new URLSearchParams({
      agencyId: agencyId.toString(),
    });

    const response = await authFetch(`${BASE_URL}oath/forms/${formId}/view?${params}`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to load form PDF' };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('getFormPdf error:', error);
    return { success: false, error: 'Network error loading PDF' };
  }
};

/**
 * Download a form PDF as a file
 * @param {number} formId - Form ID
 * @returns {Promise<{success: boolean, data?: Blob, fileName?: string, error?: string}>}
 */
export const downloadForm = async (formId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/forms/${formId}/download`);

    if (!response.ok) {
      const result = await parseResponse(response);
      return { success: false, error: result.message || 'Failed to download form' };
    }

    const blob = await response.blob();
    const contentDisposition = response.headers.get('content-disposition');
    const fileName = contentDisposition
      ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
      : `form_${formId}.pdf`;

    return { success: true, data: blob, fileName };
  } catch (error) {
    console.error('downloadForm error:', error);
    return { success: false, error: 'Network error downloading form' };
  }
};

// ============================================
// ADDRESS / SUB-AGENT SELECTION
// ============================================

export const getSubAgents = async (agencyId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/forms/agents?agencyId=${agencyId}`);
    const result = await parseResponse(response);
    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to load agents' };
    }
    return { success: true, data: result };
  } catch (error) {
    console.error('getSubAgents error:', error);
    return { success: false, error: 'Network error loading agents' };
  }
};

export const getSubAgentAddress = async (subAgentId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/forms/agents/${subAgentId}/address`);
    const result = await parseResponse(response);
    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to load address' };
    }
    return { success: true, data: result };
  } catch (error) {
    console.error('getSubAgentAddress error:', error);
    return { success: false, error: 'Network error loading address' };
  }
};

export const getUserAddress = async (agencyId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/forms/user/address?agencyId=${agencyId}`);
    const result = await parseResponse(response);
    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to load address' };
    }
    return { success: true, data: result };
  } catch (error) {
    console.error('getUserAddress error:', error);
    return { success: false, error: 'Network error loading address' };
  }
};

// ============================================
// FORM ORDERING
// ============================================

export const submitFormOrder = async (orderPayload) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/forms/orders`, {
      method: 'POST',
      body: JSON.stringify(orderPayload),
    });
    const result = await parseResponse(response);
    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to submit order' };
    }
    return { success: true, data: result };
  } catch (error) {
    console.error('submitFormOrder error:', error);
    return { success: false, error: 'Network error submitting order' };
  }
};

// ============================================
// ORDER HISTORY
// ============================================

export const getOrderHistory = async (agencyId) => {
  try {
    const params = new URLSearchParams({ agencyId: agencyId.toString() });
    const response = await authFetch(`${BASE_URL}oath/forms/orders?${params}`);
    const result = await parseResponse(response);
    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to load order history' };
    }
    return { success: true, data: result };
  } catch (error) {
    console.error('getOrderHistory error:', error);
    return { success: false, error: 'Network error loading order history' };
  }
};

export const getOrderDetails = async (orderId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/forms/orders/${orderId}`);
    const result = await parseResponse(response);
    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to load order details' };
    }
    return { success: true, data: result };
  } catch (error) {
    console.error('getOrderDetails error:', error);
    return { success: false, error: 'Network error loading order details' };
  }
};
